import { Icon } from '@/app_components/common/Icon';
import { Image } from '@/app_components/common/Image';
import { Link } from '@/app_components/common/Link';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { HorizontalSeparator, VerticalSeparator } from '@/app_components/ui/Separator';
import { VideoHero } from '@/app_components/Video/VideoHero';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import type { Breadcrumb } from '@/app_lib/types/Article';
import type { Video } from '@/app_lib/types/Video';
import type { SubNavItem } from '@/app_lib/utils/pages/sectionUtils';
import { cn } from '@/app_lib/utils/utils';
import { Breadcrumbs } from '../common/Breadcrumbs';

interface PageHeadingProps {
  breadcrumbs: Breadcrumb[];
  description?: string | null;
  imageSrc?: string;
  imageAlt?: string;
  name: string;
  heading?: string;
  sponsor?: {
    enabled: boolean;
    link?: string;
    thumbnail?: string;
  };
  subNavItems?: SubNavItem[];
  video?: Video | null;
  pageNum?: number;
  pageCount?: number;
}

export const PageHeading = ({
  breadcrumbs,
  description,
  imageSrc,
  imageAlt,
  name,
  heading,
  sponsor,
  subNavItems,
  video,
  pageNum,
  pageCount,
}: PageHeadingProps) => (
  <div className='flex flex-col gap-md'>
    <Breadcrumbs items={breadcrumbs} />
    <CategoryHeading
      pageCount={pageCount}
      pageNum={pageNum}
      sponsorEnabled={sponsor?.enabled}
      sponsorLink={sponsor?.link}
      sponsorThumbnail={sponsor?.thumbnail}
    >
      {name}
    </CategoryHeading>
    {heading && <h1 className='typography-lg font-semibold'>{heading}</h1>}
    <div className='flex flex-col layout-gap-xs'>
      <div className='flex items-start overflow-x-auto scrollbar-hidden -mx-4 px-4 md:mx-0 md:px-0'>
        <div
          className='flex gap-md'
          data-cy='sub-category-nav-items'
        >
          {subNavItems?.map((item, i) => {
            const elements = [
              <Link
                className='whitespace-nowrap'
                data-cy={`nav-item-${item.name}`}
                href={item.uri}
                key={`${i}-${item.name}`}
                recipe='nav'
                variant='secondary'
              >
                {item.name}
              </Link>,
            ];

            if (i < subNavItems.length - 1) {
              elements.push(<VerticalSeparator className='stroke-helper' />);
            }

            return elements;
          })}
        </div>
      </div>
      {imageSrc && (
        <Image
          // TODO: Put real alt text here
          alt={imageAlt ?? 'alt text'}
          className='flex w-full'
          src={imageSrc}
        />
      )}
      {description && (
        <div>
          {/* TODO: Probably shouldn't be setting this styling here */}
          <DescriptionParser
            className='typography-level-6 [&_a]:font-semibold'
            content={description}
          />
        </div>
      )}
      {video && <VideoHero video={video} />}
    </div>
  </div>
);

export const MoreInLink = ({
  children,
  className,
  href,
  dataCy,
}: {
  children: string;
  className?: string;
  href: string;
  dataCy?: string;
}) => (
  <ButtonV2
    className={className}
    data-cy={dataCy}
    href={href}
    isTertiary
  >
    {`More in ${children}`}
    <Icon icon='ChevronRight' />
  </ButtonV2>
);

export const StandardHeading = ({
  children,
  className,
  currentPage,
  href,
  hideMore = false,
  moreInLinkText,
  totalPages,
}: {
  children: string;
  className?: string;
  currentPage?: number;
  href?: string;
  hideMore?: boolean;
  moreInLinkText?: string;
  totalPages?: number;
}) => (
  <div className={cn('flex justify-between border-l-4 stroke-red pl-4 items-center', className)}>
    <div className='flex flex-col'>
      {href ? (
        <Link
          className='typography-level-2 font-semibold'
          data-cy='standard-heading-link'
          data-testid='standard-heading-link'
          href={href}
        >
          {children}
        </Link>
      ) : (
        <span
          className='typography-level-2 font-semibold'
          data-cy='standard-heading-container'
        >
          {children}
        </span>
      )}
      {currentPage !== 1 && currentPage && totalPages && (
        <span className='text-secondary typography-sm'>
          Page {currentPage} of {totalPages}
        </span>
      )}
    </div>
    {!hideMore && href && (
      <MoreInLink
        className='hidden lg:flex'
        dataCy='standard-heading-more-link'
        href={href}
      >
        {moreInLinkText ?? children}
      </MoreInLink>
    )}
  </div>
);

export const BorderedHeading = ({ children, href }: { children: string; href?: string }) => (
  <div className='flex flex-col gap-sm'>
    {href ? (
      <Link
        className='font-inria-serif typography-level-2 font-bold'
        href={href}
      >
        {children}
      </Link>
    ) : (
      <span className='font-inria-serif typography-level-2 font-bold'>{children}</span>
    )}
    <hr className='stroke-inverse' />
  </div>
);

export const StrikethroughHeading = ({
  children,
  className,
  id,
}: {
  children: string;
  className?: string;
  id?: string;
}) => (
  <div className={cn('flex gap-xl items-center', className)}>
    <hr className='stroke-secondary grow' />
    <div className='flex flex-col gap-xs'>
      <span
        className='typography-level-2 font-semibold'
        id={id}
      >
        {children}
      </span>
      <hr className='stroke-red border-t-4' />
    </div>
    <hr className='stroke-secondary grow' />
  </div>
);

export const CategoryHeading = ({
  children,
  className,
  sponsorEnabled = false,
  sponsorLink = '',
  sponsorThumbnail = '',
  pageNum,
  pageCount,
}: {
  children: string;
  className?: string;
  sponsorEnabled?: boolean;
  sponsorLink?: string;
  sponsorThumbnail?: string;
  pageNum?: number;
  pageCount?: number;
}) => (
  <div
    className={cn('flex flex-col gap-3', className)}
    data-cy='heading-container'
  >
    <div className='flex flex-wrap items-center gap-6'>
      {/* NOTE: Should this always be an h1? */}
      <h1
        className='typography-lg font-semibold capitalize'
        data-cy='title'
      >
        {children}
      </h1>
      {sponsorEnabled && (
        <div className='flex items-center gap-lg'>
          <span className='font-graphik typography-sm text-secondary uppercase mt-1'>Presented By</span>
          <Link href={sponsorLink}>
            <Image
              alt='Section sponsor image'
              height={50}
              src={sponsorThumbnail}
              width={100}
            />
          </Link>
        </div>
      )}
      {pageNum !== 1 && pageNum && pageCount && (
        <span className='text-secondary typography-sm self-end place-self-end ml-auto whitespace-nowrap'>
          Page {pageNum} of {pageCount}
        </span>
      )}
    </div>
    <HorizontalSeparator className='stroke-secondary' />
  </div>
);
